import { emitMetaEvent } from "./utils/metaPixel";

const trackEnabled = true;

const trackEvent = (eventType, eventName, eventParams = {}) => {
    // Staff acting in a borrower's context is not the borrower's analytics.
    if (window.is_impersonating) {
        return;
    }

    if (!trackEnabled) {
        console.log("GA4 Tracking is disabled");
        return;
    }

    // Above the window.gtag branch deliberately. Everything below — including the POST that
    // queues the server copy — sits inside it, so a blocked or missing GA4 would otherwise take
    // the Meta conversion down with it. Only the two allow-listed events do anything here.
    emitMetaEvent(eventType, eventName);

    if (window.gtag) {
        const cleanEventParams = eventParams;

        // add loan status as prefix to event name
        if (eventParams.hasOwnProperty("loan_status")) {
            eventName = `${eventParams.loan_status.toUpperCase()}_${eventName}`;
        }

        if (eventParams.user_id) {
            eventParams.trackerid = eventParams.user_id;
            delete eventParams.user_id;
        }

        eventParams.eventType = eventType;
        gtag("event", eventName, eventParams);

        axios
            .post("/analytics/events", {
                loan_id: eventParams.loan_id ?? null,
                event_type: eventType,
                event_name: eventName,
                event_params: cleanEventParams,
                event_time: new Date().toISOString(),
            })
            .catch((error) => {
                // Silently ignore analytics tracking errors (e.g., 404, auth issues during tests)
                // Analytics should not break the application flow
                if (console.debug) {
                    console.debug("Analytics event failed to send:", error.message);
                }
            });

        console.log(`Event tracked: ${eventName}`, eventParams);

        return;
    }

    console.warn("Google Analytics is not loaded.");
};

/**
 * Track leadgen analytics events — posts to /leadgen/analytics/events
 * instead of /analytics/events (which requires auth + loan_id).
 */
export const trackLeadgenAnalyticsEvent = (eventType, eventName, eventParams = {}) => {
    if (!trackEnabled) {
        console.log("GA4 Tracking is disabled");
        return;
    }

    // Enrich every leadgen event with the applicant's state so analytics can be
    // segmented by licensed state. Falls back to 'UN' (unknown/unlicensed) when
    // the user hasn't reached the personal-info step yet or is in a state we
    // don't operate in.
    // Guard against an empty array: ?? only catches null/undefined, not [],
    // so use a length check so a misconfigured Nova setting still falls back.
    const LICENSED_STATES = (window.__licensedStates__ && window.__licensedStates__.length > 0)
        ? window.__licensedStates__
        : ['MO', 'ID', 'MS'];
    const rawState = (window.__leadgenState__ ?? eventParams.state ?? '').toUpperCase();
    const state = LICENSED_STATES.includes(rawState) ? rawState : 'UN';
    eventParams = { ...eventParams, state };

    // The browser half of the deduplicated conversion pair; the POST below queues the server half
    // under the same event_id. Outside the window.gtag branch, like that POST already is.
    emitMetaEvent(eventType, eventName);

    if (window.gtag) {
        eventParams.eventType = eventType;
        gtag("event", eventName, eventParams);
    }

    axios
        .post("/leadgen/analytics/events", {
            event_type: eventType,
            event_name: eventName,
            event_params: eventParams,
            event_time: new Date().toISOString(),
            // On a restricted continuation window.lead.key stays null all through the visit
            // (the held lead's key is withheld); send the '-' sentinel instead so the server
            // can resolve the event back onto the held lead.
            lead_key: eventParams.lead_key ?? window.lead?.key ?? (window.__leadgenRestricted__ ? '-' : null),
        })
        .catch((error) => {
            if (console.debug) {
                console.debug("Leadgen analytics event failed to send:", error.message);
            }
        });

    console.log(`Leadgen event tracked: ${eventName}`, eventParams);
};

export const analytics = {
    marketPlaceSignUpEvents: {
        changeAmount: (params = {}) =>
            trackEvent("MarketPlaceSignUp", "CHANGE_AMOUNT", { event_category: "onboarding", ...params }),
        nextButtonClickedEvent: (params = {}) =>
            trackEvent("MarketPlaceSignUp", "NEXT_CLICKED", { event_category: "onboarding", ...params }),
        nextButtonClickedSuccess: (params = {}) =>
            trackEvent("MarketPlaceSignUp", "NEXT_SUCCESS", { event_category: "onboarding", ...params }),
        nextButtonClickedError: (params = {}) =>
            trackEvent("MarketPlaceSignUp", "NEXT_ERROR", { event_category: "onboarding", ...params }),

        handleOpenManualAccountModal: (params = {}) =>
            trackEvent("MarketPlaceSignUp", "OPEN_MANUAL_ACCOUNT_MODAL", { event_category: "onboarding", ...params }),
        handleCloseManualAccountModal: (params = {}) =>
            trackEvent("MarketPlaceSignUp", "CLOSE_MANUAL_ACCOUNT_MODAL", { event_category: "onboarding", ...params }),
        handleSubmitManualAccountModal: (params = {}) =>
            trackEvent("MarketPlaceSignUp", "SUBMIT_MANUAL_ACCOUNT_MODAL", { event_category: "onboarding", ...params }),
        handleSubmitManualAccountModalError: (params = {}) =>
            trackEvent("MarketPlaceSignUp", "SUBMIT_MANUAL_ACCOUNT_MODAL_ERROR", {
                event_category: "onboarding",
                ...params,
            }),

        setDepositSelectAccount: (params = {}) =>
            trackEvent("MarketPlaceSignUp", "SELECT_ACCOUNT", { event_category: "onboarding", ...params }),
        setDepositSelectAccountClick: (params = {}) =>
            trackEvent("MarketPlaceSignUp", "SELECT_ACCOUNT_CLICK", { event_category: "onboarding", ...params }),
        setDepositConfirmClicked: (params = {}) =>
            trackEvent("MarketPlaceSignUp", "CONFIRM_CLICKED", { event_category: "onboarding", ...params }),
        setDepositConfirmConfirmClicked: (params = {}) =>
            trackEvent("MarketPlaceSignUp", "CONFIRM_CONFIRM_CLICKED", { event_category: "onboarding", ...params }),
        setDepositCloseConfirmModal: (params = {}) =>
            trackEvent("MarketPlaceSignUp", "CLOSE_CONFIRM_CLICKED", { event_category: "onboarding", ...params }),
        setDepositSelectAccountError: (params = {}) =>
            trackEvent("MarketPlaceSignUp", "SELECT_ACCOUNT_ERROR", { event_category: "onboarding", ...params }),
        setDepositReconnectToPlaid: (params = {}) =>
            trackEvent("MarketPlaceSignUp", "RECONNECT_TO_PLAID", { event_category: "onboarding", ...params }),

        changeAddressEvent: (params = {}) =>
            trackEvent("MarketPlaceSignUp", "ADDRESS_VERIFICATION_CHANGE_ADDRESS_EVENT", {
                event_category: "onboarding",
                ...params,
            }),
        addressVerificationChangePage: (params = {}) =>
            trackEvent("MarketPlaceSignUp", "ADDRESS_VERIFICATION_CHANGE_PAGE", {
                event_category: "onboarding",
                ...params,
            }),
        addressVerificationConfirmAddress: (params = {}) =>
            trackEvent("MarketPlaceSignUp", "ADDRESS_VERIFICATION_CONFIRM_ADDRESS", {
                event_category: "onboarding",
                ...params,
            }),
        addressVerificationConfirmAddressError: (params = {}) =>
            trackEvent("MarketPlaceSignUp", "ADDRESS_VERIFICATION_CONFIRM_ADDRESS_ERROR", {
                event_category: "onboarding",
                ...params,
            }),

        confirmAddressEvent: (params = {}) =>
            trackEvent("MarketPlaceSignUp", "ADDRESS_VERIFICATION_CONFIRM_ADDRESS_EVENT", {
                event_category: "onboarding",
                ...params,
            }),

        clickOfferLoanToCreditStrong: (params = {}) =>
            trackEvent("MarketPlaceSignUp", "OFFER_LOAN_TO_CREDIT_STRONG", { event_category: "onboarding", ...params }),
    },

    docusignEvents: {
        signLoanAgreement: (params = {}) =>
            trackEvent("Docusign", "SIGN_LOAN_AGREEMENT", { event_category: "onboarding", ...params }),
        uploadDocument: (params = {}) =>
            trackEvent("Docusign", "UPLOAD_DOCUMENT", { event_category: "onboarding", ...params }),
        documentSigned: (params = {}) =>
            trackEvent("Docusign", "DOCUMENT_SIGNED", { event_category: "onboarding", ...params }),
    },

    loanTermsEvents: {
        changeAmount: (params = {}) => trackLeadgenAnalyticsEvent('LeadgenLoanTerms', 'CHANGE_AMOUNT', {event_category:'leadgen',...params}),
        changePeriod: (params = {}) => trackLeadgenAnalyticsEvent('LeadgenLoanTerms', 'CHANGE_PERIOD', {event_category:'leadgen',...params}),
        changeFirstPaymentDate: (params = {}) => trackLeadgenAnalyticsEvent('LeadgenLoanTerms', 'CHANGE_FIRST_PAYMENT_DATE', {event_category:'leadgen',...params}),
        nextClicked: (params = {}) => trackLeadgenAnalyticsEvent('LeadgenLoanTerms', 'NEXT_CLICKED', {event_category:'leadgen',...params}),
        nextSuccess: (params = {}) => trackLeadgenAnalyticsEvent('LeadgenLoanTerms', 'NEXT_SUCCESS', {event_category:'leadgen',...params}),
        nextError: (params = {}) => trackLeadgenAnalyticsEvent('LeadgenLoanTerms', 'NEXT_ERROR', {event_category:'leadgen',...params}),
        dropOff: (params = {}) => trackLeadgenAnalyticsEvent('LeadgenLoanTerms', 'DROP_OFF', {event_category:'leadgen',...params}),
    },

    personalInfoEvents: {
        pageView: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenPersonalInfo", "PERSONAL_INFO_PAGE_VIEW", {
                event_category: "leadgen",
                ...params,
            }),
        changeFirstName: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenPersonalInfo", "CHANGE_FIRST_NAME", {
                event_category: "leadgen",
                ...params,
            }),
        changeLastName: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenPersonalInfo", "CHANGE_LAST_NAME", {
                event_category: "leadgen",
                ...params,
            }),
        changeEmail: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenPersonalInfo", "CHANGE_EMAIL", { event_category: "leadgen", ...params }),
        changePhoneNumber: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenPersonalInfo", "CHANGE_PHONE_NUMBER", {
                event_category: "leadgen",
                ...params,
            }),
        changeAddress: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenPersonalInfo", "CHANGE_ADDRESS", {
                event_category: "leadgen",
                ...params,
            }),
        changeCity: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenPersonalInfo", "CHANGE_CITY", { event_category: "leadgen", ...params }),
        changeState: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenPersonalInfo", "CHANGE_STATE", { event_category: "leadgen", ...params }),
        changeZip: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenPersonalInfo", "CHANGE_ZIP", { event_category: "leadgen", ...params }),
        changeDriversLicenseNumber: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenPersonalInfo", "CHANGE_DRIVERS_LICENSE_NUMBER", {
                event_category: "leadgen",
                ...params,
            }),
        changeDriversLicenseState: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenPersonalInfo", "CHANGE_DRIVERS_LICENSE_STATE", {
                event_category: "leadgen",
                ...params,
            }),
        changeDateOfBirth: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenPersonalInfo", "CHANGE_DATE_OF_BIRTH", {
                event_category: "leadgen",
                ...params,
            }),
        addressSuggestionShown: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenPersonalInfo", "ADDRESS_SUGGESTION_SHOWN", {
                event_category: "leadgen",
                ...params,
            }),
        addressSuggestionAccepted: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenPersonalInfo", "ADDRESS_SUGGESTION_ACCEPTED", {
                event_category: "leadgen",
                ...params,
            }),
        addressSuggestionRejected: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenPersonalInfo", "ADDRESS_SUGGESTION_REJECTED", {
                event_category: "leadgen",
                ...params,
            }),
        nextClicked: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenPersonalInfo", "NEXT_CLICKED", { event_category: "leadgen", ...params }),
        nextSuccess: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenPersonalInfo", "NEXT_SUCCESS", { event_category: "leadgen", ...params }),
        nextError: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenPersonalInfo", "NEXT_ERROR", { event_category: "leadgen", ...params }),
        dropOff: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenPersonalInfo", "DROP_OFF", { event_category: "leadgen", ...params }),
    },

    identityVerificationEvents: {
        pageView: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenIdentityVerification", "IDENTITY_VERIFICATION_PAGE_VIEW", { event_category: "leadgen", ...params }),
        changeSsn: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenIdentityVerification", "CHANGE_SSN", { event_category: "leadgen", ...params }),
        changeDateOfBirth: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenIdentityVerification", "CHANGE_DATE_OF_BIRTH", { event_category: "leadgen", ...params }),
        changeDriversLicenseNumber: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenIdentityVerification", "CHANGE_DRIVERS_LICENSE_NUMBER", { event_category: "leadgen", ...params }),
        changeDriversLicenseState: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenIdentityVerification", "CHANGE_DRIVERS_LICENSE_STATE", { event_category: "leadgen", ...params }),
        nextClicked: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenIdentityVerification", "NEXT_CLICKED", { event_category: "leadgen", ...params }),
        nextSuccess: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenIdentityVerification", "NEXT_SUCCESS", { event_category: "leadgen", ...params }),
        nextError: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenIdentityVerification", "NEXT_ERROR", { event_category: "leadgen", ...params }),
        dropOff: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenIdentityVerification", "DROP_OFF", { event_category: "leadgen", ...params }),
    },

    incomeEvents: {
        pageView: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenIncome", "INCOME_PAGE_VIEW", { event_category: "leadgen", ...params }),
        changePayPeriod: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenIncome", "CHANGE_PAY_PERIOD", { event_category: "leadgen", ...params }),
        changeMonthlyIncome: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenIncome", "CHANGE_MONTHLY_INCOME", {
                event_category: "leadgen",
                ...params,
            }),
        changeIncomeSource: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenIncome", "CHANGE_INCOME_SOURCE", {
                event_category: "leadgen",
                ...params,
            }),
        changeEmployer: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenIncome", "CHANGE_EMPLOYER", { event_category: "leadgen", ...params }),
        changeMonthsEmployed: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenIncome", "CHANGE_MONTHS_EMPLOYED", {
                event_category: "leadgen",
                ...params,
            }),
        changeWorkPhone: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenIncome", "CHANGE_WORK_PHONE", { event_category: "leadgen", ...params }),
        changeJobTitle: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenIncome", "CHANGE_JOB_TITLE", { event_category: "leadgen", ...params }),
        nextClicked: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenIncome", "NEXT_CLICKED", { event_category: "leadgen", ...params }),
        nextSuccess: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenIncome", "NEXT_SUCCESS", { event_category: "leadgen", ...params }),
        nextError: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenIncome", "NEXT_ERROR", { event_category: "leadgen", ...params }),
        dropOff: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenIncome", "DROP_OFF", { event_category: "leadgen", ...params }),
    },

    housingEvents: {
        pageView: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenHousing", "HOUSING_PAGE_VIEW", { event_category: "leadgen", ...params }),
        changeHousing: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenHousing", "CHANGE_HOUSING", { event_category: "leadgen", ...params }),
        changeMonthsAtResidence: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenHousing", "CHANGE_MONTHS_AT_RESIDENCE", {
                event_category: "leadgen",
                ...params,
            }),
        nextClicked: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenHousing", "NEXT_CLICKED", { event_category: "leadgen", ...params }),
        nextSuccess: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenHousing", "NEXT_SUCCESS", { event_category: "leadgen", ...params }),
        nextError: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenHousing", "NEXT_ERROR", { event_category: "leadgen", ...params }),
        dropOff: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenHousing", "DROP_OFF", { event_category: "leadgen", ...params }),
    },

    accountInfoEvents: {
        pageView: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenAccountInfo", "ACCOUNT_INFO_PAGE_VIEW", {
                event_category: "leadgen",
                ...params,
            }),
        changeBankName: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenAccountInfo", "CHANGE_BANK_NAME", {
                event_category: "leadgen",
                ...params,
            }),
        changeRoutingNumber: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenAccountInfo", "CHANGE_ROUTING_NUMBER", {
                event_category: "leadgen",
                ...params,
            }),
        changeAccountNumber: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenAccountInfo", "CHANGE_ACCOUNT_NUMBER", {
                event_category: "leadgen",
                ...params,
            }),
        accountConfirmationMismatch: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenAccountInfo", "ACCOUNT_CONFIRMATION_MISMATCH", {
                event_category: "leadgen",
                ...params,
            }),
        plaidConnectClicked: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenAccountInfo", "PLAID_CONNECT_CLICKED", {
                event_category: "leadgen",
                ...params,
            }),
        plaidConnectBlocked: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenAccountInfo", "PLAID_CONNECT_BLOCKED", {
                event_category: "leadgen",
                ...params,
            }),
        plaidSkipped: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenAccountInfo", "PLAID_SKIPPED", { event_category: "leadgen", ...params }),
        plaidSuccess: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenAccountInfo", "PLAID_SUCCESS", { event_category: "leadgen", ...params }),
        plaidExit: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenAccountInfo", "PLAID_EXIT", { event_category: "leadgen", ...params }),
        plaidError: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenAccountInfo", "PLAID_ERROR", { event_category: "leadgen", ...params }),
        nextClicked: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenAccountInfo", "NEXT_CLICKED", { event_category: "leadgen", ...params }),
        nextSuccess: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenAccountInfo", "NEXT_SUCCESS", { event_category: "leadgen", ...params }),
        nextError: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenAccountInfo", "NEXT_ERROR", { event_category: "leadgen", ...params }),
        dropOff: (params = {}) =>
            trackLeadgenAnalyticsEvent("LeadgenAccountInfo", "DROP_OFF", { event_category: "leadgen", ...params }),
    },

    dinamicPageEvents: (eventType, pageName, evtCategory = "onboarding") => {
        return {
            handlePlaid: (params = {}) =>
                trackEvent(eventType, `PLAID_OPEN`, { event_category: evtCategory, ...params }),
            handlePlaidSuccess: (params = {}) =>
                trackEvent(eventType, `PLAID_SUCCESS`, { event_category: evtCategory, ...params }),
            handlePlaidExit: (params = {}) =>
                trackEvent(eventType, `PLAID_EXIT`, { event_category: evtCategory, ...params }),
            handlePlaidExitError: (params = {}) =>
                trackEvent(eventType, `PLAID_EXIT_ERROR`, { event_category: evtCategory, ...params }),
            handleMxError: (params = {}) =>
                trackEvent(eventType, `MX_ERROR`, { event_category: evtCategory, ...params }),
            handleMxOauthError: (params = {}) =>
                trackEvent(eventType, `MX_OAUTH_ERROR`, { event_category: evtCategory, ...params }),
            handleMxConnected: (params = {}) =>
                trackEvent(eventType, `MX_CONNECTED`, { event_category: evtCategory, ...params }),
            handleMxSelectInstitution: (params = {}) =>
                trackEvent(eventType, `MX_SELECT_INSTITUTION`, { event_category: evtCategory, ...params }),
            handleMxProcced: (params = {}) =>
                trackEvent(eventType, `MX_PROCCED}`, { event_category: evtCategory, ...params }),

            handleOpenManualAccountModal: (params = {}) =>
                trackEvent(eventType, `OPEN_MANUAL_ACCOUNT_MODAL`, { event_category: evtCategory, ...params }),
            handleCloseManualAccountModal: (params = {}) =>
                trackEvent(eventType, `CLOSE_MANUAL_ACCOUNT_MODAL`, { event_category: evtCategory, ...params }),
            handleSubmitManualAccountModal: (params = {}) =>
                trackEvent(eventType, `SUBMIT_MANUAL_ACCOUNT_MODAL`, { event_category: evtCategory, ...params }),
            handleSubmitManualAccountModalError: (params = {}) =>
                trackEvent(eventType, `SUBMIT_MANUAL_ACCOUNT_MODAL_ERROR`, { event_category: evtCategory, ...params }),

            pageView: (params = {}) => trackEvent(eventType, `PAGE_VIEW`, { event_category: evtCategory, ...params }),
            // success
        };
    },
};

export default analytics;
